import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { formatMoney, intervalLabel, majorToSmallestUnit, smallestUnitToMajor, currencySymbol } from "@/lib/money";
import StudioLayout from "@/components/layouts/StudioLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Archive, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function StudioPlans() {
  const [tenant, setTenant] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    try {
      const [t, p] = await Promise.all([api.get("/tenant/me"), api.get("/cms/plans")]);
      setTenant(t.data);
      setPlans(p.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const onArchive = async (plan) => {
    if (!window.confirm(`Archive "${plan.name}"? Subscribers on this plan keep access until period end.`)) return;
    try {
      await api.delete(`/cms/plans/${plan.id}`);
      toast.success("Plan archived");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <StudioLayout tenant={tenant}>
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">Studio admin · Plans</p>
          <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl tracking-tight">Subscription plans</h1>
          <p className="mt-1 text-white/50">
            Default currency: <span className="text-white">{tenant?.default_currency || "INR"}</span>
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-white text-black hover:bg-amber-300 uppercase text-[11px] tracking-[0.25em]" data-testid="plans-new-button">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New plan
            </Button>
          </DialogTrigger>
          {createOpen && (
            <PlanForm
              defaultCurrency={tenant?.default_currency || "INR"}
              onClose={() => setCreateOpen(false)}
              onSaved={() => { setCreateOpen(false); refresh(); }}
            />
          )}
        </Dialog>
      </div>

      {loading ? (
        <div className="text-white/40 text-sm uppercase tracking-widest">Loading…</div>
      ) : plans.length === 0 ? (
        <div className="border border-dashed border-white/15 p-12 text-center" data-testid="plans-empty">
          <h2 className="font-display font-bold text-2xl">No plans yet.</h2>
          <p className="mt-2 text-white/50 text-sm">Create your first subscription plan to start monetizing.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="plans-grid">
          {plans.map((p) => (
            <div
              key={p.id}
              className={`bg-[#121212] border ${p.status === "archived" ? "border-white/5 opacity-60" : "border-white/10"} p-6`}
              data-testid={`plan-card-${p.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <Badge className="rounded-none uppercase text-[10px] tracking-[0.2em] bg-white/10 text-white/60">
                    {p.interval}
                  </Badge>
                  <h3 className="mt-3 font-display font-bold text-2xl tracking-tight">{p.name}</h3>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-white/60 hover:text-white" onClick={() => setEditing(p)} data-testid={`plan-edit-${p.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {p.status === "active" && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-300 hover:text-red-200" onClick={() => onArchive(p)} data-testid={`plan-archive-${p.id}`}>
                      <Archive className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display font-black text-4xl">{formatMoney(p.price_amount, p.currency)}</span>
                <span className="text-white/50 text-sm">{intervalLabel(p.interval)}</span>
              </div>
              {p.description && <p className="mt-3 text-sm text-white/60 font-light">{p.description}</p>}
              {p.perks?.length > 0 && (
                <ul className="mt-4 space-y-1.5 text-sm text-white/70">
                  {p.perks.map((perk, i) => <li key={i} className="flex items-start gap-2"><span className="text-emerald-400">·</span> {perk}</li>)}
                </ul>
              )}
              <div className="mt-5 pt-4 border-t border-white/10 text-xs text-white/40 flex items-center justify-between">
                <Badge className={`rounded-none uppercase text-[10px] tracking-[0.2em] ${p.status === "active" ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-white/50"}`}>
                  {p.status}
                </Badge>
                {p.trial_days > 0 && <span>{p.trial_days}d trial</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing && (
          <PlanForm
            plan={editing}
            defaultCurrency={tenant?.default_currency || "INR"}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); refresh(); }}
          />
        )}
      </Dialog>
    </StudioLayout>
  );
}

function PlanForm({ plan, defaultCurrency, onClose, onSaved }) {
  const isEdit = !!plan;
  const [form, setForm] = useState({
    name: plan?.name ?? "",
    description: plan?.description ?? "",
    interval: plan?.interval ?? "monthly",
    currency: plan?.currency ?? defaultCurrency,
    price_major: plan ? smallestUnitToMajor(plan.price_amount) : "",
    perks: (plan?.perks || []).join("\n"),
    trial_days: plan?.trial_days ?? 0,
    status: plan?.status ?? "active",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const body = {
        name: form.name.trim(),
        description: form.description,
        interval: form.interval,
        currency: form.currency,
        price_amount: majorToSmallestUnit(form.price_major),
        perks: form.perks.split("\n").map((s) => s.trim()).filter(Boolean),
        trial_days: parseInt(form.trial_days, 10) || 0,
        status: form.status,
      };
      if (isEdit) {
        await api.patch(`/cms/plans/${plan.id}`, body);
        toast.success("Plan saved");
      } else {
        await api.post("/cms/plans", body);
        toast.success("Plan created");
      }
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="bg-[#0d0d0d] border-white/10 rounded-none text-white max-w-xl" data-testid="plan-form">
      <DialogHeader>
        <DialogTitle className="font-display font-bold text-2xl tracking-tight">{isEdit ? "Edit plan" : "New plan"}</DialogTitle>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Name">
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} required className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="plan-form-name" />
        </Field>
        <Field label="Description">
          <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="plan-form-description" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Interval">
            <Select value={form.interval} onValueChange={(v) => set("interval", v)}>
              <SelectTrigger className="bg-transparent border-white/15 rounded-none" data-testid="plan-form-interval"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 rounded-none text-white">
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Currency">
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger className="bg-transparent border-white/15 rounded-none" data-testid="plan-form-currency"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 rounded-none text-white">
                <SelectItem value="INR">INR · ₹</SelectItem>
                <SelectItem value="USD">USD · $</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        <Field label={`Price ${currencySymbol(form.currency)} (in ${form.currency === "INR" ? "rupees" : "dollars"})`} hint={`Stored as smallest unit · ${form.price_major ? majorToSmallestUnit(form.price_major) : 0} ${form.currency === "INR" ? "paise" : "cents"}`}>
          <Input type="number" step="0.01" min={0} value={form.price_major} onChange={(e) => set("price_major", e.target.value)} required className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="plan-form-price" />
        </Field>
        <Field label="Perks" hint="One per line.">
          <Textarea value={form.perks} onChange={(e) => set("perks", e.target.value)} rows={3} placeholder={"Full catalogue\nHD streaming\nCancel anytime"} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="plan-form-perks" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Trial days">
            <Input type="number" min={0} value={form.trial_days} onChange={(e) => set("trial_days", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="plan-form-trial" />
          </Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="bg-transparent border-white/15 rounded-none" data-testid="plan-form-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 rounded-none text-white">
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        {error && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 p-3" data-testid="plan-form-error">{error}</div>}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} className="rounded-none uppercase text-[11px] tracking-[0.25em] text-white/60 hover:text-white">Cancel</Button>
          <Button type="submit" disabled={submitting} className="rounded-none bg-white text-black hover:bg-amber-300 uppercase text-[11px] tracking-[0.25em]" data-testid="plan-form-save">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? "Save plan" : "Create plan")}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50">{label}</Label>
      {children}
      {hint && <div className="text-[10px] text-white/30">{hint}</div>}
    </div>
  );
}
